/**
 * Сервис администрирования.
 * Ожидаемые эндпоинты бэкенда описаны в комментариях к методам.
 */
import { EducationSection } from '@/model/education-section.model';
import { Authority } from '@/model/authority.model';
import { User } from '@/model/user.model';
import buildPaginationQueryOpts from '@/utils/sorts';
import { IEducationPdfMaterial } from '@/model/education-pdf-material.model';
import { IDiskSpace, IEducationVideo } from '@/model/video-upload.model';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import axios from 'axios';

const API = (() => {
  const v = process.env.VUE_APP_API_URL || '';
  return v && !v.endsWith('/') ? v + '/' : v;
})();

async function getToken(): Promise<string> {
  const { value } = await Preferences.get({ key: 'jwt' });
  return value || '';
}

function httpError(message: string, status: number): Error & { status: number } {
  const e = new Error(message) as Error & { status: number };
  e.status = status;
  return e;
}

function isMobile(): boolean {
  return Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';
}

export default class AdminService {
  /**
   * GET api/authorities-full — список всех ролей (с groupId > 0).
   */
  async getAuthoritiesFull(): Promise<Authority[]> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/authorities-full`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as Authority[];
    }
    const { data } = await axios.get<Authority[]>('api/authorities-full');
    return data || [];
  }

  /**
   * GET api/admin/authorities — список всех ролей с количеством пользователей (для страницы админки ролей).
   */
  async getAdminAuthorities(): Promise<Authority[]> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/authorities`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as Authority[];
    }
    const { data } = await axios.get<Authority[]>('api/admin/authorities');
    return data || [];
  }

  /**
   * POST api/admin/authorities — создание роли. Body: { name: string, description?: string, groupId?: number }
   */
  async createAuthority(body: { name: string; description?: string; groupId?: number }): Promise<Authority> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.post({
        url: `${API}api/admin/authorities`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: body,
        readTimeout: 60000,
      });
      return res.data as Authority;
    }
    const { data } = await axios.post<Authority>('api/admin/authorities', body);
    return data;
  }

  /**
   * PUT api/admin/authorities/:name — обновить роль (description, groupId).
   */
  async updateAuthority(
    name: string,
    body: { description?: string; groupId?: number }
  ): Promise<Authority> {
    const encodedName = encodeURIComponent(name);
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.put({
        url: `${API}api/admin/authorities/${encodedName}`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: body,
        readTimeout: 60000,
      });
      return res.data as Authority;
    }
    const { data } = await axios.put<Authority>(`api/admin/authorities/${encodedName}`, body);
    return data;
  }

  /**
   * DELETE api/admin/authorities/:name — удалить роль (снимается у всех пользователей, затем удаляется запись; кеш ролей обновляется автоматически).
   */
  async deleteAuthority(name: string): Promise<void> {
    const encodedName = encodeURIComponent(name);
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.delete({
        url: `${API}api/admin/authorities/${encodedName}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return;
    }
    await axios.delete(`api/admin/authorities/${encodedName}`);
  }

  /**
   * POST api/admin/users/refresh-roles-cache — обновить кеш ролей у всех пользователей.
   * Body пустой или { userId?: number } для одного пользователя.
   */
  async refreshRolesCache(userId?: number): Promise<void> {
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.post({
        url: `${API}api/admin/users/refresh-roles-cache`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: userId != null ? { userId } : {},
        readTimeout: 60000,
      });
      return;
    }
    await axios.post('api/admin/users/refresh-roles-cache', userId != null ? { userId } : {});
  }

  /**
   * GET api/admin/users — страница пользователей (для селекторов в админке).
   */
  async getAdminUsersPage(page = 0, size = 1000): Promise<User[]> {
    const paginationQuery = { page, size, sort: ['lastName,asc', 'firstName,asc'] };
    const qs = buildPaginationQueryOpts(paginationQuery);
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/users?${qs}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as User[];
    }
    const { data } = await axios.get<User[]>(`api/admin/users?${qs}`);
    return data || [];
  }

  /**
   * GET api/admin/education-sections — список разделов образования (для админа и для выбора при загрузке).
   * Разделы с roleName и folderKey для отображения в Образовании и при загрузке видео.
   */
  async getEducationSections(): Promise<EducationSection[]> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/education-sections`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as EducationSection[];
    }
    const { data } = await axios.get<EducationSection[]>('api/admin/education-sections');
    return data || [];
  }

  /**
   * POST api/admin/education-sections — добавить раздел. Body: { name, roleName, folderKey, sortOrder? }
   * PUT api/admin/education-sections/:id — обновить.
   * DELETE api/admin/education-sections/:id — удалить.
   */
  async createEducationSection(section: Partial<EducationSection>): Promise<EducationSection> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.post({
        url: `${API}api/admin/education-sections`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: section,
        readTimeout: 60000,
      });
      return res.data as EducationSection;
    }
    const { data } = await axios.post<EducationSection>('api/admin/education-sections', section);
    return data;
  }

  async updateEducationSection(id: number, section: Partial<EducationSection>): Promise<EducationSection> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.put({
        url: `${API}api/admin/education-sections/${id}`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: section,
        readTimeout: 60000,
      });
      return res.data as EducationSection;
    }
    const { data } = await axios.put<EducationSection>(`api/admin/education-sections/${id}`, section);
    return data;
  }

  async deleteEducationSection(id: number): Promise<void> {
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.delete({
        url: `${API}api/admin/education-sections/${id}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return;
    }
    await axios.delete(`api/admin/education-sections/${id}`);
  }

  /**
   * GET api/admin/education/sections — разделы образования для текущего пользователя (по его ролям).
   * Возвращает только те разделы, к которым у пользователя есть доступ.
   */
  async getEducationSectionsForCurrentUser(): Promise<EducationSection[]> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/education/sections`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as EducationSection[];
    }
    const { data } = await axios.get<EducationSection[]>('api/admin/education/sections');
    return data || [];
  }

  /**
   * GET api/education/videos?sectionFolderKey=X — публичный список READY видео раздела (для пользователей с доступом).
   */
  async getPublicEducationVideos(sectionFolderKey: string): Promise<IEducationVideo[]> {
    const q = `?sectionFolderKey=${encodeURIComponent(sectionFolderKey)}`;
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/education/videos${q}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      if (res.status === 403) return [];
      if (res.status >= 400) return [];
      const arr = res.data;
      return Array.isArray(arr) ? (arr as IEducationVideo[]) : [];
    }
    try {
      const { data } = await axios.get<IEducationVideo[]>(`api/education/videos${q}`);
      return Array.isArray(data) ? data : [];
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 403) return [];
      throw e;
    }
  }

  /**
   * На мобильных подменяем хост stream URL, чтобы устройство могло достучаться до стрима.
   * Если URL от внешнего стримера (path /video) и задан VUE_APP_EDUCATION_STREAM_URL — подставляем его (для стримера на другом хосте).
   * Иначе подставляем API base (для встроенного стрима бэкенда).
   */
  private fixStreamUrlForMobile(streamUrl: string): string {
    if (!streamUrl || typeof streamUrl !== 'string') return streamUrl;
    try {
      const u = new URL(streamUrl);
      const streamerBase = (process.env.VUE_APP_EDUCATION_STREAM_URL || '').replace(/\/$/, '');
      if (streamerBase && (u.pathname === '/video' || u.pathname.endsWith('/video'))) {
        return `${streamerBase}${u.pathname}${u.search}`;
      }
      const apiBase = (process.env.VUE_APP_API_EXT_URL || process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
      if (!apiBase) return streamUrl;
      return `${apiBase}${u.pathname}${u.search}`;
    } catch {
      return streamUrl;
    }
  }

  /**
   * GET api/education/video/{id}/stream-request — URL для education видео.
   * GET api/education/video/stream-request?type=google&fileId=xxx — URL для Google Drive видео.
   * Response: { url: string }
   */
  async getEducationVideoStreamUrl(videoId: number): Promise<string> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/education/video/${videoId}/stream-request`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 10000,
      });
      if (res.data?.url) return this.fixStreamUrlForMobile(res.data.url);
      throw new Error('Invalid stream response');
    }
    const { data } = await axios.get<{ url: string }>(`api/education/video/${videoId}/stream-request`);
    if (data?.url) return data.url;
    throw new Error('Invalid stream response');
  }

  async getGoogleVideoStreamUrl(fileId: string): Promise<string> {
    const q = `?type=google&fileId=${encodeURIComponent(fileId)}`;
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/education/video/stream-request${q}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 10000,
      });
      if (res.data?.url) return this.fixStreamUrlForMobile(res.data.url);
      throw new Error('Invalid stream response');
    }
    const { data } = await axios.get<{ url: string }>(`api/education/video/stream-request${q}`);
    if (data?.url) return data.url;
    throw new Error('Invalid stream response');
  }

  /**
   * GET api/education/video-progress?videoKey=edu:123 — прогресс просмотра (сервер, синхронизация между устройствами).
   * Response: { lastPositionSeconds, durationSeconds, viewedAt } или 404.
   */
  async getEducationVideoProgress(videoKey: string): Promise<{ lastPositionSeconds: number; durationSeconds?: number; viewedAt?: string } | null> {
    const q = `?videoKey=${encodeURIComponent(videoKey)}`;
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/education/video-progress${q}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 10000,
      });
      if (res.status === 404) return null;
      if (res.status >= 400) return null;
      return res.data as { lastPositionSeconds: number; durationSeconds?: number; viewedAt?: string };
    }
    try {
      const { data } = await axios.get<{ lastPositionSeconds: number; durationSeconds?: number; viewedAt?: string }>(`api/education/video-progress${q}`);
      return data;
    } catch (e: unknown) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) return null;
      return null;
    }
  }

  /**
   * PUT api/education/video-progress — сохранить позицию просмотра. viewedAt ставится при >= 90%.
   */
  async putEducationVideoProgress(videoKey: string, positionSeconds: number, durationSeconds?: number): Promise<void> {
    const body = { videoKey, positionSeconds, durationSeconds };
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.put({
        url: `${API}api/education/video-progress`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: body,
        readTimeout: 10000,
      });
      return;
    }
    await axios.put('api/education/video-progress', body);
  }

  /**
   * GET api/admin/media/disk-space — свободное место на сервере (для /opt/media).
   * Response: { availableBytes, totalBytes, usedBytes }
   */
  async getDiskSpace(): Promise<IDiskSpace> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/media/disk-space`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 10000,
      });
      return res.data as IDiskSpace;
    }
    const { data } = await axios.get<IDiskSpace>('api/admin/media/disk-space');
    return data;
  }

  /**
   * GET api/admin/education/videos — список видео раздела «Образование» (все разделы или по sectionFolderKey).
   * Query: ?sectionFolderKey=Ted (опционально).
   * Response: массив { id, displayName, fileName, sectionFolderKey, sectionName, status, sourceType, ... }
   */
  async getEducationVideos(sectionFolderKey?: string): Promise<IEducationVideo[]> {
    const q = sectionFolderKey ? `?sectionFolderKey=${encodeURIComponent(sectionFolderKey)}` : '';
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/education/videos${q}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as IEducationVideo[];
    }
    const { data } = await axios.get<IEducationVideo[]>(`api/admin/education/videos${q}`);
    return data || [];
  }

  /**
   * POST api/admin/education/videos/upload — загрузка видео на сервер в /opt/media.
   * Content-Type: multipart/form-data. Поля: file (File), sectionFolderKey (string), displayName (string).
   * Сервер после сохранения переводит запись в статус PROCESSING и запускает конвертацию (ffmpeg).
   * Response: IEducationVideo.
   * Используется fetch() с FormData: на вебе — обычный fetch; на нативных платформах при включённом
   * CapacitorHttp (plugins.CapacitorHttp.enabled: true) — патченный fetch с нативным HTTP (см. capacitorjs.com/docs/apis/http).
   * Для файлов в десятки ГБ один запрос не подходит (память, таймауты). См. docs/LARGE_FILE_UPLOAD.md и
   * @capacitor/file-transfer (chunkedMode) при наличии path к файлу.
   */
  async uploadEducationVideo(
    file: File,
    sectionFolderKey: string,
    displayName: string,
    onChunkedProgress?: (uploadedBytes: number, totalBytes: number) => void
  ): Promise<IEducationVideo> {
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
    const CHUNKED_THRESHOLD = 100 * 1024 * 1024; // 100 MB — выше используем чанковую загрузку
    if (file.size > CHUNKED_THRESHOLD) {
      return this.uploadEducationVideoChunked(file, sectionFolderKey, displayName, CHUNK_SIZE, onChunkedProgress);
    }
    return this.uploadEducationVideoSingle(file, sectionFolderKey, displayName);
  }

  /**
   * Расширенная загрузка с поддержкой отмены.
   * onUploadIdReady(uploadId) — вызывается при начале чанковой загрузки (uploadId для отмены).
   * isCancelled() — если вернёт true, загрузка прервётся.
   */
  async uploadEducationVideoWithCallback(
    file: File,
    sectionFolderKey: string,
    displayName: string,
    onProgress?: (uploadedBytes: number, totalBytes: number) => void,
    onUploadIdReady?: (uploadId: string) => void,
    isCancelled?: () => boolean
  ): Promise<IEducationVideo> {
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
    const CHUNKED_THRESHOLD = 100 * 1024 * 1024; // 100 MB
    if (file.size > CHUNKED_THRESHOLD) {
      return this.uploadEducationVideoChunkedWithCancel(
        file,
        sectionFolderKey,
        displayName,
        CHUNK_SIZE,
        onProgress,
        onUploadIdReady,
        isCancelled
      );
    }
    return this.uploadEducationVideoSingle(file, sectionFolderKey, displayName);
  }

  /**
   * Чанковая загрузка с поддержкой отмены и возобновления.
   */
  private async uploadEducationVideoChunkedWithCancel(
    file: File,
    sectionFolderKey: string,
    displayName: string,
    chunkSize: number,
    onProgress?: (uploadedBytes: number, totalBytes: number) => void,
    onUploadIdReady?: (uploadId: string) => void,
    isCancelled?: () => boolean
  ): Promise<IEducationVideo> {
    const total = file.size;
    const totalChunks = Math.ceil(total / chunkSize);
    const storageKey = this.storageKey(file, sectionFolderKey, displayName);

    let uploadId: string;
    let startIndex: number;

    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const { uploadId: storedId } = JSON.parse(stored);
        const status = await this.getChunkedUploadStatus(storedId);
        if (status && status.nextChunkIndex < totalChunks) {
          uploadId = storedId;
          startIndex = status.nextChunkIndex;
        } else {
          const initRes = await this.initChunkedUpload();
          uploadId = initRes.uploadId;
          startIndex = 0;
          localStorage.setItem(storageKey, JSON.stringify({ uploadId }));
        }
      } catch {
        const initRes = await this.initChunkedUpload();
        uploadId = initRes.uploadId;
        startIndex = 0;
        localStorage.setItem(storageKey, JSON.stringify({ uploadId }));
      }
    } else {
      const initRes = await this.initChunkedUpload();
      uploadId = initRes.uploadId;
      startIndex = 0;
      localStorage.setItem(storageKey, JSON.stringify({ uploadId }));
    }

    onUploadIdReady?.(uploadId);
    let uploadedBytes = startIndex * chunkSize;
    for (let index = startIndex; index < totalChunks; index++) {
      if (isCancelled?.()) {
        throw new Error('Upload cancelled');
      }
      const start = index * chunkSize;
      const end = Math.min(start + chunkSize, total);
      const chunkBlob = file.slice(start, end);
      await this.uploadChunk(uploadId, index, totalChunks, chunkBlob);
      uploadedBytes = end;
      onProgress?.(uploadedBytes, total);
    }
    if (isCancelled?.()) {
      throw new Error('Upload cancelled');
    }
    const result = await this.finishChunkedUpload(uploadId, sectionFolderKey, displayName.trim());
    localStorage.removeItem(storageKey);
    return result;
  }

  /**
   * Один POST с FormData (для файлов до порога CHUNKED_THRESHOLD).
   */
  private async uploadEducationVideoSingle(
    file: File,
    sectionFolderKey: string,
    displayName: string,
    signal?: AbortSignal
  ): Promise<IEducationVideo> {
    const form = new FormData();
    form.append('file', file);
    form.append('sectionFolderKey', sectionFolderKey);
    form.append('displayName', displayName);

    const token = await getToken();
    const url = `${API}api/admin/education/videos/upload`;
    const controller = new AbortController();
    if (signal) signal.addEventListener('abort', () => controller.abort());
    const timeoutId = setTimeout(() => controller.abort(), 300000);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) throw httpError(`Upload failed: ${res.status}`, res.status);
      return (await res.json()) as IEducationVideo;
    } catch (e) {
      clearTimeout(timeoutId);
      throw e;
    }
  }

  /**
   * GET api/admin/education/videos/upload/status?uploadId=… — статус для возобновления загрузки.
   * Response: { uploadId, nextChunkIndex } если возобновляема, иначе 404.
   */
  async getChunkedUploadStatus(uploadId: string): Promise<{ uploadId: string; nextChunkIndex: number } | null> {
    const url = `${API}api/admin/education/videos/upload/status?uploadId=${encodeURIComponent(uploadId)}`;
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 10000,
      });
      if (res.status === 404) return null;
      return res.data as { uploadId: string; nextChunkIndex: number };
    }
    try {
      const token = await getToken();
      const { data } = await axios.get<{ uploadId: string; nextChunkIndex: number }>(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return data;
    } catch {
      return null;
    }
  }

  /**
   * POST api/admin/education/videos/upload/init — создать сессию чанковой загрузки.
   * Response: { uploadId: string }.
   */
  async initChunkedUpload(): Promise<{ uploadId: string }> {
    const token = await getToken();
    const url = `${API}api/admin/education/videos/upload/init`;
    if (isMobile()) {
      const res = await CapacitorHttp.post({
        url,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 30000,
      });
      return res.data as { uploadId: string };
    }
    const { data } = await axios.post<{ uploadId: string }>(url, {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return data;
  }

  /**
   * PUT api/admin/education/videos/upload/chunk?uploadId=…&index=…&total=…
   * Body — сырые байты чанка (application/octet-stream).
   */
  async uploadChunk(
    uploadId: string,
    index: number,
    total: number,
    chunkBlob: Blob
  ): Promise<void> {
    const token = await getToken();
    const url = `${API}api/admin/education/videos/upload/chunk?uploadId=${encodeURIComponent(uploadId)}&index=${index}&total=${total}`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' } : { 'Content-Type': 'application/octet-stream' },
      body: chunkBlob,
    });
    if (!res.ok) throw httpError(`Chunk upload failed: ${res.status}`, res.status);
  }

  /**
   * POST api/admin/education/videos/upload/finish?uploadId=…&sectionFolderKey=…&displayName=…
   * Сервер склеивает чанки, создаёт запись в БД, ставит конвертацию. Response: IEducationVideo.
   */
  async finishChunkedUpload(
    uploadId: string,
    sectionFolderKey: string,
    displayName: string
  ): Promise<IEducationVideo> {
    const token = await getToken();
    const q = new URLSearchParams({
      uploadId,
      sectionFolderKey,
      displayName: displayName.trim(),
    });
    const url = `${API}api/admin/education/videos/upload/finish?${q.toString()}`;
    if (isMobile()) {
      const res = await CapacitorHttp.post({
        url,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return res.data as IEducationVideo;
    }
    const { data } = await axios.post<IEducationVideo>(url, {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return data;
  }

  private static UPLOAD_STORAGE_KEY = 'liga-chunked-upload';

  private storageKey(file: File, sectionFolderKey: string, displayName: string): string {
    return `${AdminService.UPLOAD_STORAGE_KEY}-${file.name}-${file.size}-${sectionFolderKey}-${displayName}`;
  }

  saveResumableUpload(file: File, sectionFolderKey: string, displayName: string, uploadId: string): void {
    try {
      const key = this.storageKey(file, sectionFolderKey, displayName);
      localStorage.setItem(key, JSON.stringify({ uploadId }));
    } catch {
      // ignore
    }
  }

  clearResumableUpload(uploadId: string): void {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith(AdminService.UPLOAD_STORAGE_KEY)) {
          const v = localStorage.getItem(k);
          if (v) {
            try {
              const { uploadId: id } = JSON.parse(v);
              if (id === uploadId) {
                localStorage.removeItem(k);
                return;
              }
            } catch {
              // ignore
            }
          }
        }
      }
    } catch {
      // ignore
    }
  }

  /**
   * Чанковая загрузка: init (или resume) → chunk × N → finish.
   * Поддерживает возобновление после обрыва соединения (localStorage + status).
   */
  private async uploadEducationVideoChunked(
    file: File,
    sectionFolderKey: string,
    displayName: string,
    chunkSize: number,
    onProgress?: (uploadedBytes: number, totalBytes: number) => void
  ): Promise<IEducationVideo> {
    const total = file.size;
    const totalChunks = Math.ceil(total / chunkSize);
    const storageKey = this.storageKey(file, sectionFolderKey, displayName);

    let uploadId: string;
    let startIndex: number;

    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const { uploadId: storedId } = JSON.parse(stored);
        const status = await this.getChunkedUploadStatus(storedId);
        if (status && status.nextChunkIndex < totalChunks) {
          uploadId = storedId;
          startIndex = status.nextChunkIndex;
        } else {
          const initRes = await this.initChunkedUpload();
          uploadId = initRes.uploadId;
          startIndex = 0;
          localStorage.setItem(storageKey, JSON.stringify({ uploadId }));
        }
      } catch {
        const initRes = await this.initChunkedUpload();
        uploadId = initRes.uploadId;
        startIndex = 0;
        localStorage.setItem(storageKey, JSON.stringify({ uploadId }));
      }
    } else {
      const initRes = await this.initChunkedUpload();
      uploadId = initRes.uploadId;
      startIndex = 0;
      localStorage.setItem(storageKey, JSON.stringify({ uploadId }));
    }

    let uploadedBytes = startIndex * chunkSize;
    for (let index = startIndex; index < totalChunks; index++) {
      const start = index * chunkSize;
      const end = Math.min(start + chunkSize, total);
      const chunkBlob = file.slice(start, end);
      await this.uploadChunk(uploadId, index, totalChunks, chunkBlob);
      uploadedBytes = end;
      onProgress?.(uploadedBytes, total);
    }

    const result = await this.finishChunkedUpload(uploadId, sectionFolderKey, displayName.trim());
    localStorage.removeItem(storageKey);
    return result;
  }

  /**
   * PATCH api/admin/education/videos/:id — обновить видео (displayName, sectionFolderKey для перемещения).
   * Body: { displayName?: string, sectionFolderKey?: string }
   */
  async updateEducationVideo(
    id: string | number,
    patch: { displayName?: string; sectionFolderKey?: string }
  ): Promise<IEducationVideo> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.request({
        method: 'PATCH',
        url: `${API}api/admin/education/videos/${id}`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: patch,
        readTimeout: 60000,
      });
      return res.data as IEducationVideo;
    }
    const { data } = await axios.patch<IEducationVideo>(`api/admin/education/videos/${id}`, patch);
    return data;
  }

  /**
   * DELETE api/admin/education/videos/:id — удалить видео.
   * Для READY/FAILED/CANCELLED — удаляет файлы с диска и запись из БД.
   * Для PROCESSING — ставит CANCELLED (воркер остановит ffmpeg, удалит файлы).
   */
  async deleteEducationVideo(id: string | number): Promise<void> {
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.delete({
        url: `${API}api/admin/education/videos/${id}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return;
    }
    await axios.delete(`api/admin/education/videos/${id}`);
  }

  /**
   * DELETE api/admin/education/videos/upload/abort?uploadId=… — отменить чанковую загрузку.
   * Удаляет частичный файл и сессию.
   */
  async abortChunkedUpload(uploadId: string): Promise<void> {
    const url = `api/admin/education/videos/upload/abort?uploadId=${encodeURIComponent(uploadId)}`;
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.delete({
        url: `${API}${url}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 30000,
      });
      return;
    }
    await axios.delete(url);
  }

  /**
   * POST api/admin/education/videos/:id/cancel-conversion — отменить конвертацию.
   * Ставит CANCELLED — воркер остановит ffmpeg и удалит частичный .mp4.
   */
  async cancelConversion(id: string | number): Promise<IEducationVideo> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.post({
        url: `${API}api/admin/education/videos/${id}/cancel-conversion`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 30000,
      });
      return res.data as IEducationVideo;
    }
    const { data } = await axios.post<IEducationVideo>(`api/admin/education/videos/${id}/cancel-conversion`);
    return data;
  }

  /**
   * POST api/admin/education/videos/:id/retry-conversion — повторить конвертацию.
   * Для FAILED или CANCELLED. Удаляет частичный .mp4, ставит PROCESSING, отправляет в очередь.
   */
  async retryConversion(id: string | number): Promise<IEducationVideo> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.post({
        url: `${API}api/admin/education/videos/${id}/retry-conversion`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 30000,
      });
      return res.data as IEducationVideo;
    }
    const { data } = await axios.post<IEducationVideo>(`api/admin/education/videos/${id}/retry-conversion`);
    return data;
  }

  /**
   * GET api/admin/education/pdfs?sectionFolderKey=… — список PDF (опционально по разделу).
   */
  async getEducationPdfs(sectionFolderKey?: string): Promise<IEducationPdfMaterial[]> {
    const q = sectionFolderKey ? `?sectionFolderKey=${encodeURIComponent(sectionFolderKey)}` : '';
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/education/pdfs${q}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as IEducationPdfMaterial[];
    }
    const { data } = await axios.get<IEducationPdfMaterial[]>(`api/admin/education/pdfs${q}`);
    return data || [];
  }

  /**
   * POST api/admin/education/pdfs/upload — загрузить PDF.
   * Принудительно проверяется расширение .pdf (без учёта регистра) на клиенте и на сервере.
   */
  async uploadPdf(
    file: File,
    sectionFolderKey: string,
    displayName: string
  ): Promise<IEducationPdfMaterial> {
    const ext = (file.name || '').split('.').pop()?.toLowerCase();
    if (ext !== 'pdf') {
      throw new Error('Разрешены только файлы с расширением .pdf');
    }
    const form = new FormData();
    form.append('file', file);
    form.append('sectionFolderKey', sectionFolderKey);
    form.append('displayName', displayName.trim());

    const token = await getToken();
    const url = `${API}api/admin/education/pdfs/upload`;
    const res = await fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) {
      const err = new Error(`Upload failed: ${res.status}`) as Error & { response?: { status?: number } };
      err.response = { status: res.status };
      throw err;
    }
    return (await res.json()) as IEducationPdfMaterial;
  }

  /**
   * PATCH api/admin/education/pdfs/:id — обновить PDF (displayName, sectionFolderKey, allowDownload).
   */
  async updateEducationPdf(
    id: number,
    patch: { displayName?: string; sectionFolderKey?: string; allowDownload?: boolean }
  ): Promise<IEducationPdfMaterial> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.request({
        method: 'PATCH',
        url: `${API}api/admin/education/pdfs/${id}`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: patch,
        readTimeout: 60000,
      });
      return res.data as IEducationPdfMaterial;
    }
    const { data } = await axios.patch<IEducationPdfMaterial>(`api/admin/education/pdfs/${id}`, patch);
    return data;
  }

  /**
   * DELETE api/admin/education/pdfs/:id — удалить PDF (файл и запись).
   */
  async deleteEducationPdf(id: number): Promise<void> {
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.delete({
        url: `${API}api/admin/education/pdfs/${id}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return;
    }
    await axios.delete(`api/admin/education/pdfs/${id}`);
  }

  /**
   * GET api/education/pdfs?sectionFolderKey=… — публичный список PDF раздела (для пользователей с доступом).
   */
  async getPublicEducationPdfs(sectionFolderKey: string): Promise<IEducationPdfMaterial[]> {
    const q = `?sectionFolderKey=${encodeURIComponent(sectionFolderKey)}`;
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/education/pdfs${q}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      if (res.status === 403) return [];
      if (res.status >= 400) return [];
      const arr = res.data;
      return Array.isArray(arr) ? (arr as IEducationPdfMaterial[]) : [];
    }
    try {
      const { data } = await axios.get<IEducationPdfMaterial[]>(`api/education/pdfs${q}`);
      return Array.isArray(data) ? data : [];
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 403) return [];
      throw e;
    }
  }

  /**
   * GET api/education/pdf/:id/view — получить PDF для просмотра в браузере (inline).
   * Возвращает Blob для создания object URL и отображения в iframe без раскрытия прямого URL.
   */
  async getEducationPdfViewBlob(id: number): Promise<Blob> {
    const url = `${API}api/education/pdf/${id}/view`;
    const token = await getToken();
    const res = await fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`PDF view failed: ${res.status}`);
    return res.blob();
  }

  /**
   * GET api/education/pdf/:id/download — получить PDF для скачивания (attachment).
   * Доступно только если у материала разрешено скачивание (allowDownload = true). При 403 — скачивание запрещено.
   */
  async getEducationPdfDownloadBlob(id: number): Promise<Blob> {
    const url = `${API}api/education/pdf/${id}/download`;
    const token = await getToken();
    const res = await fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(res.status === 403 ? 'Download not allowed' : `PDF download failed: ${res.status}`);
    return res.blob();
  }

  // ——— Тестирование (публичный API) ———
  async getMyTests(): Promise<IMyTestVM[]> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/education/tests`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as IMyTestVM[];
    }
    const { data } = await axios.get<IMyTestVM[]>(`${API}api/education/tests`);
    return data || [];
  }

  async getTest(id: number): Promise<ITestPublicVM> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/education/tests/${id}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return res.data as ITestPublicVM;
    }
    const { data } = await axios.get<ITestPublicVM>(`${API}api/education/tests/${id}`);
    return data;
  }

  async getTestDraft(id: number): Promise<{ attemptId: number; selectedAnswersByQuestion: Record<number, number[]>; startedAt?: string } | null> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/education/tests/${id}/draft`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      if (res.status === 204) return null;
      return res.data as { attemptId: number; selectedAnswersByQuestion: Record<number, number[]>; startedAt?: string };
    }
    const res = await axios.get(`${API}api/education/tests/${id}/draft`, { validateStatus: (s) => s === 200 || s === 204 });
    if (res.status === 204) return null;
    return res.data;
  }

  async saveTestDraft(id: number, selectedAnswersByQuestion: Record<number, number[]>): Promise<{ attemptId: number; selectedAnswersByQuestion: Record<number, number[]> }> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.post({
        url: `${API}api/education/tests/${id}/draft`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: selectedAnswersByQuestion,
        readTimeout: 60000,
      });
      return res.data as { attemptId: number; selectedAnswersByQuestion: Record<number, number[]> };
    }
    const { data } = await axios.post(`${API}api/education/tests/${id}/draft`, selectedAnswersByQuestion);
    return data;
  }

  async submitTest(id: number, selectedAnswersByQuestion: Record<number, number[]>): Promise<{ attemptId: number; score: number; passed: boolean }> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.post({
        url: `${API}api/education/tests/${id}/submit`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: selectedAnswersByQuestion,
        readTimeout: 25000,
      });
      if (res.status < 200 || res.status >= 300) {
        const msg = (res.data as { message?: string })?.message || `HTTP ${res.status}`;
        throw new Error(msg);
      }
      return res.data as { attemptId: number; score: number; passed: boolean };
    }
    const { data } = await axios.post(`${API}api/education/tests/${id}/submit`, selectedAnswersByQuestion);
    return data;
  }

  /**
   * GET api/admin/tests — список всех тестов (админка).
   */
  async getAdminTests(): Promise<IAdminTest[]> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/tests`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as IAdminTest[];
    }
    const { data } = await axios.get<IAdminTest[]>(`${API}api/admin/tests`);
    return data || [];
  }

  /**
   * GET api/admin/tests/:id — один тест для редактирования (админка).
   */
  async getAdminTest(id: number): Promise<IAdminTest | null> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/tests/${id}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || null) as IAdminTest | null;
    }
    try {
      const { data } = await axios.get<IAdminTest>(`${API}api/admin/tests/${id}`);
      return data ?? null;
    } catch {
      return null;
    }
  }

  async createTest(vm: { name: string; description?: string; passingThreshold?: number; maxAttempts?: number; durationMinutes?: number | null; roleNames?: string[] }): Promise<IAdminTest> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.post({
        url: `${API}api/admin/tests`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: vm,
        readTimeout: 60000,
      });
      return res.data as IAdminTest;
    }
    const { data } = await axios.post<IAdminTest>(`${API}api/admin/tests`, vm);
    return data;
  }

  async updateTest(id: number, vm: { name: string; description?: string; passingThreshold?: number; maxAttempts?: number; durationMinutes?: number | null; roleNames?: string[] }): Promise<IAdminTest> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.request({
        method: 'PUT',
        url: `${API}api/admin/tests/${id}`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: vm,
        readTimeout: 60000,
      });
      return res.data as IAdminTest;
    }
    const { data } = await axios.put<IAdminTest>(`${API}api/admin/tests/${id}`, vm);
    return data;
  }

  async deleteTest(id: number): Promise<void> {
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.delete({
        url: `${API}api/admin/tests/${id}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return;
    }
    await axios.delete(`${API}api/admin/tests/${id}`);
  }

  async getTestAssignments(testId: number): Promise<IUserTestAssignmentVM[]> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/tests/${testId}/assignments`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as IUserTestAssignmentVM[];
    }
    const { data } = await axios.get<IUserTestAssignmentVM[]>(`${API}api/admin/tests/${testId}/assignments`);
    return data || [];
  }

  async addTestAssignments(testId: number, userIds: number[]): Promise<void> {
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.post({
        url: `${API}api/admin/tests/${testId}/assignments`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: { userIds },
        readTimeout: 60000,
      });
      return;
    }
    await axios.post(`${API}api/admin/tests/${testId}/assignments`, { userIds });
  }

  async removeTestAssignment(testId: number, userId: number): Promise<void> {
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.delete({
        url: `${API}api/admin/tests/${testId}/assignments/${userId}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return;
    }
    await axios.delete(`${API}api/admin/tests/${testId}/assignments/${userId}`);
  }

  async resetTestAttempts(testId: number, userId: number): Promise<void> {
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.post({
        url: `${API}api/admin/tests/${testId}/assignments/${userId}/reset-attempts`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return;
    }
    await axios.post(`${API}api/admin/tests/${testId}/assignments/${userId}/reset-attempts`);
  }

  /** GET api/admin/tests/:id/questions */
  async getTestQuestions(testId: number): Promise<IAdminQuestion[]> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/tests/${testId}/questions`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as IAdminQuestion[];
    }
    const { data } = await axios.get<IAdminQuestion[]>(`${API}api/admin/tests/${testId}/questions`);
    return data || [];
  }

  /** POST api/admin/tests/:id/questions */
  async createQuestion(testId: number, vm: IQuestionCreateVM): Promise<IAdminQuestion> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.post({
        url: `${API}api/admin/tests/${testId}/questions`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: vm,
        readTimeout: 60000,
      });
      return res.data as IAdminQuestion;
    }
    const { data } = await axios.post<IAdminQuestion>(`${API}api/admin/tests/${testId}/questions`, vm);
    return data;
  }

  /** PUT api/admin/tests/:testId/questions/:qid */
  async updateQuestion(testId: number, qid: number, vm: IQuestionCreateVM): Promise<IAdminQuestion> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.request({
        method: 'PUT',
        url: `${API}api/admin/tests/${testId}/questions/${qid}`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: vm,
        readTimeout: 60000,
      });
      return res.data as IAdminQuestion;
    }
    const { data } = await axios.put<IAdminQuestion>(`${API}api/admin/tests/${testId}/questions/${qid}`, vm);
    return data;
  }

  /** DELETE api/admin/tests/:testId/questions/:qid */
  async deleteQuestion(testId: number, qid: number): Promise<void> {
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.delete({
        url: `${API}api/admin/tests/${testId}/questions/${qid}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return;
    }
    await axios.delete(`${API}api/admin/tests/${testId}/questions/${qid}`);
  }

  /** GET api/admin/tests/questions/:qid/answers */
  async getQuestionAnswers(qid: number): Promise<IAdminAnswer[]> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.get({
        url: `${API}api/admin/tests/questions/${qid}/answers`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return (res.data || []) as IAdminAnswer[];
    }
    const { data } = await axios.get<IAdminAnswer[]>(`${API}api/admin/tests/questions/${qid}/answers`);
    return data || [];
  }

  /** POST api/admin/tests/questions/:qid/answers */
  async createAnswer(qid: number, vm: IAnswerCreateVM): Promise<IAdminAnswer> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.post({
        url: `${API}api/admin/tests/questions/${qid}/answers`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: vm,
        readTimeout: 60000,
      });
      return res.data as IAdminAnswer;
    }
    const { data } = await axios.post<IAdminAnswer>(`${API}api/admin/tests/questions/${qid}/answers`, vm);
    return data;
  }

  /** PUT api/admin/tests/answers/:aid */
  async updateAnswer(aid: number, vm: IAnswerCreateVM): Promise<IAdminAnswer> {
    if (isMobile()) {
      const token = await getToken();
      const res = await CapacitorHttp.request({
        method: 'PUT',
        url: `${API}api/admin/tests/answers/${aid}`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: vm,
        readTimeout: 60000,
      });
      return res.data as IAdminAnswer;
    }
    const { data } = await axios.put<IAdminAnswer>(`${API}api/admin/tests/answers/${aid}`, vm);
    return data;
  }

  /** DELETE api/admin/tests/answers/:aid */
  async deleteAnswer(aid: number): Promise<void> {
    if (isMobile()) {
      const token = await getToken();
      await CapacitorHttp.delete({
        url: `${API}api/admin/tests/answers/${aid}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 60000,
      });
      return;
    }
    await axios.delete(`${API}api/admin/tests/answers/${aid}`);
  }
}

export interface IAdminQuestion {
  id: number;
  text: string;
  multipleChoice?: boolean;
  sortOrder?: number;
  answers?: IAdminAnswer[];
}

export interface IAdminAnswer {
  id: number;
  text: string;
  isCorrect?: boolean;
}

export interface IQuestionCreateVM {
  text: string;
  multipleChoice?: boolean;
  sortOrder?: number;
  answers?: IAnswerCreateVM[];
}

export interface IAnswerCreateVM {
  text: string;
  isCorrect?: boolean;
}

export interface IUserTestAssignmentVM {
  id: number;
  userId: number;
  login: string;
  firstName?: string;
  lastName?: string;
  assignedAt: string;
  /** Количество завершённых попыток */
  usedAttempts: number;
  /** Хотя бы одна попытка пройдена успешно */
  passed: boolean;
  /** Есть черновик (незавершённая попытка) */
  hasDraft: boolean;
  /** Балл последней отправленной попытки */
  lastScore?: number;
}

export interface IMyTestVM {
  id: number;
  name: string;
  description?: string;
  passingThreshold: number;
  maxAttempts?: number;
  hasDraft: boolean;
  attemptsLeft: boolean;
  passed: boolean;
  lastScore?: number;
  usedAttempts: number;
}

export interface ITestPublicVM {
  id: number;
  name: string;
  description?: string;
  passingThreshold: number;
  maxAttempts?: number;
  /** Лимит времени в минутах. null = без ограничения. */
  timeLimitMinutes?: number | null;
  questions: IQuestionPublicVM[];
}

export interface IQuestionPublicVM {
  id: number;
  text: string;
  multipleChoice?: boolean;
  sortOrder?: number;
  answers: IAnswerPublicVM[];
}

export interface IAnswerPublicVM {
  id: number;
  text: string;
}

export interface IAdminTest {
  id: number;
  name: string;
  description?: string;
  passingThreshold: number;
  maxAttempts?: number;
  /** Лимит времени на прохождение в минутах. null = без ограничения. */
  durationMinutes?: number | null;
  /** Роли при успешном прохождении (GET /api/admin/tests/:id возвращает это поле) */
  roleNames?: string[];
  /** Устаревшее: роли в виде объектов (если API вернёт rolesToGrant) */
  rolesToGrant?: { name: string }[];
}
